import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Standalone three-dot card menu.
 * Falsy items are filtered out so actions can be conditional.
 * Menu is portaled + fixed so parent overflow cannot clip it.
 */
export default function CardMenu({ items = [], className }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const visibleItems = items.filter(Boolean);

  const updatePosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 176;
    const estimatedHeight = Math.min(visibleItems.length * 40 + 8, 320);
    const gap = 4;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
    const openUp = rect.bottom + gap + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
    const top = openUp ? Math.max(8, rect.top - estimatedHeight - gap) : rect.bottom + gap;
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = e => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    const handleKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReposition = () => updatePosition();
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  if (visibleItems.length === 0) return null;

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        className="p-1 rounded-md text-muted-dim hover:text-[var(--text-color)] hover:bg-black/[0.04] transition-colors"
        aria-label="Card actions"
        aria-expanded={open}>
        <MoreVertical className="w-4 h-4" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[100] w-44 py-1 rounded-lg bg-white border border-shell-card-border shadow-xl shadow-black/10 overflow-hidden"
            style={{ top: pos.top, left: pos.left }}
            onClick={e => e.stopPropagation()}>
            {visibleItems.map(({ label, icon: Icon, onClick, danger }) => (
              <button
                key={label}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setOpen(false);
                  onClick?.();
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                  danger
                    ? 'text-[#EF4444] hover:bg-[#EF4444]/10'
                    : 'text-muted hover:bg-black/[0.04] hover:text-[var(--text-color)]',
                )}>
                {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                {label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
